import { useMemo } from 'react'
import { getCountForm, getRandomNumber } from '@ucheba/utils/helpers/number'
import { EMediaType } from '@ucheba/store/institutions/types'
import { stringifyUrl } from '@ucheba/utils/helpers/router'
import { EProgramTypes } from '@ucheba/utils/types/program'
import { IInstitutionsListItemProps, IUseInstitutionsListCore } from './types'
import { institutionPath } from './contants'

export const useInstitutionHref = (id: IInstitutionsListItemProps['id']): string => {
  return useMemo(() => `${institutionPath}/${id}`, [id])
}

export const useImageColWidth = (width: string): string => {
  return useMemo(() => `${parseInt(width, 10) + 32}px`, [width])
}

export const useInstitutionsListCore: IUseInstitutionsListCore = ({
  id,
  programSummary,
  mediaList,
  specialityId,
  tagId,
  specialityIds,
  activeProgramType,
}) => {
  const programs = useMemo(
    () => ({
      entity: programSummary.count,
      clarification: getCountForm(programSummary.count, [
        'программа',
        'программы',
        'программ',
      ]),
    }),
    [programSummary.count]
  )

  const programsHref = useMemo(() => {
    if (specialityId) {
      return `${institutionPath}/${id}/programs?specialityIds=${specialityId}`
    }

    if (tagId && activeProgramType === EProgramTypes.course) {
      return stringifyUrl(`${institutionPath}/${id}/programs/course`, {
        set: {
          tagIds: Array.isArray(tagId) ? tagId : [tagId],
        },
      })
    }
    if (specialityIds) {
      return activeProgramType === EProgramTypes.vocational
        ? stringifyUrl(`${institutionPath}/${id}/programs/vocational`, {
            set: {
              specialityIds: Array.isArray(specialityIds)
                ? specialityIds
                : [specialityIds],
            },
          })
        : stringifyUrl(`${institutionPath}/${id}/programs`, {
            set: {
              specialityIds: Array.isArray(specialityIds)
                ? specialityIds
                : [specialityIds],
            },
          })
    }

    return `${institutionPath}/${id}/programs`
  }, [activeProgramType, id, specialityId, specialityIds, tagId])

  const budget = useMemo(
    () => ({
      entity: programSummary.budgetPlacesCount || 0,
      clarification: 'бюджетных мест',
    }),
    [programSummary.budgetPlacesCount]
  )

  const score = useMemo(
    () => ({
      entity: programSummary.minPassingScore
        ? `от ${programSummary.minPassingScore}`
        : '',
      clarification: 'проходной балл',
    }),
    [programSummary.minPassingScore]
  )

  const price = useMemo(
    () => ({
      entity: programSummary.priceFrom ? `от ${programSummary.priceFrom} р.` : '',
      clarification: 'за год',
    }),
    [programSummary.priceFrom]
  )

  const media = useMemo(
    () =>
      (mediaList || []).map(({ fileUrl = '', description = '', mediaType, image }) => ({
        src:
          EMediaType.image === mediaType || EMediaType.image === mediaType.value
            ? image?.fullUrl.fallbackUrl
            : fileUrl,
        thumb:
          EMediaType.image === mediaType || EMediaType.image === mediaType.value
            ? image?.thumbMin.fallbackUrl
            : `/static/img/video-covers/${getRandomNumber(60) || 1}.png`,
        subHtml: description,
      })),
    [mediaList]
  )

  return {
    programs,
    programsHref,
    budget,
    score,
    price,
    media,
  }
}
